import { useLocalStorage } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useColumnSorter } from "@/components/elements";
import { MarketCarsDashboard } from "@/components/widgets";
import { paths } from "@/config/paths";
import { defaultCurrency } from "@/lib/currency/getCurrency";
import { useMarketMakeCars } from "@/services/market/get-market-make-cars";
import { useMarketMakePerformance } from "@/services/market/get-market-make-performance";
import { useMarketMakeTopPerforming } from "@/services/market/get-market-makes-top-performing";
import { filterMarketCarsBySearch } from "@/utils/marketCarSearch";

import type { Currency } from "@/types/currency";
import type { MarketCar, MarketCarColumns } from "@/types/market";
import type { FC } from "react";

export type MarketMakesCarDashboardProps = {
  getPath?: (carId: string) => string;
  make?: string;
  makeId: string;
};

const namespace = "market-cars-table";

export const MarketMakesCarDashboard: FC<MarketMakesCarDashboardProps> = ({
  getPath,
  make = "",
  makeId,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const isFiltering = search.trim().length > 0;
  const { sort } = useColumnSorter<MarketCar, MarketCarColumns>(`${namespace}-sort`, undefined);
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const {
    data: paginatedCars,
    isLoading: isPaginatedLoading,
    isPending: isPendingCars,
    refetch: refetchCars,
  } = useMarketMakeCars({
    makeId,
    page,
    pageSize,
    parameters: { full_text: "" },
    sort,
  });

  const {
    data: allCars,
    isLoading: isAllLoading,
    refetch: refetchAllCars,
  } = useMarketMakeCars({
    makeId,
    page: 1,
    pageSize: 999999,
    parameters: { full_text: "" },
    sort,
    enabled: isFiltering,
  });

  const isLoadingCars = isFiltering ? isAllLoading : isPaginatedLoading;

  const {
    data: topCars,
    isLoading: isLoadingTopCars,
    refetch: refetchTopCars,
  } = useMarketMakeTopPerforming({ makeId });
  const {
    data: performance,
    isLoading: isLoadingPerformance,
    refetch: refetchPerformance,
  } = useMarketMakePerformance({
    makeId,
  });

  function handlePageSize(pageSize: number) {
    setPageSize(pageSize);
    setPage(1);
  }

  function updateSearchQuery(query: string) {
    setSearch(query);
    setPage(1);
  }

  function handleModelsCompare(model1Id: string, model2Id: string) {
    navigate(paths.app.markets.compare.getHref(model1Id, model2Id));
  }

  const filteredCars = useMemo(() => {
    if (!isFiltering) return paginatedCars?.results ?? [];
    return filterMarketCarsBySearch(allCars?.results ?? [], search, currency.code);
  }, [allCars?.results, currency.code, isFiltering, paginatedCars?.results, search]);

  const tableCars = useMemo(() => {
    if (!isFiltering) return filteredCars;

    const start = (page - 1) * pageSize;
    return filteredCars.slice(start, start + pageSize);
  }, [filteredCars, isFiltering, page, pageSize]);

  useEffect(() => {
    refetchCars();
    refetchAllCars();
    refetchTopCars();
    refetchPerformance();
  }, [makeId]);

  return (
    <MarketCarsDashboard
      cars={tableCars}
      carsProps={{
        makeId,
        namespace,
        onModelsCompare: handleModelsCompare,
        pending: isPendingCars,
        onPageChange: setPage,
        onPageSizeChange: handlePageSize,
        onSearch: updateSearchQuery,
        page,
        pageSize,
        totalCars: isFiltering ? filteredCars.length : paginatedCars?.total || 0,
      }}
      columns={[
        "model_name",
        "make_name",
        "produced_cars",
        "production_years",
        "current_valuation",
        "year_over_year_gain_loss",
      ]}
      getPath={getPath}
      loading={isLoadingCars}
      makeOrDecade={make}
      performance={performance}
      performanceProps={{ loading: isLoadingPerformance }}
      topCars={topCars || []}
      topCarsProps={{ loading: isLoadingTopCars }}
    />
  );
};
